import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Copy, Check, Send, AlertCircle, Webhook, RadioTower, Plug } from "lucide-react";

export default function KarixSetup() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [copied, setCopied] = useState(false);

  // Test send form
  const [toNum, setToNum] = useState("");
  const [text, setText] = useState("Hello from Gulshan ONE29 Loyalty!");
  const [sendResp, setSendResp] = useState(null);
  const [sending, setSending] = useState(false);

  // Template
  const [tplNum, setTplNum] = useState("");
  const [tplId, setTplId] = useState("");
  const [tplParams, setTplParams] = useState('{"0":"John","1":"50"}');
  const [tplResp, setTplResp] = useState(null);

  useEffect(() => {
    api.get("/whatsapp/karix/status").then((r) => setStatus(r.data));
    api.get("/whatsapp/webhook-logs", { params: { limit: 20 } }).then((r) => setLogs(r.data));
  }, []);

  const copyWebhook = () => {
    navigator.clipboard.writeText(status?.webhook_url || "");
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const doTestSend = async () => {
    setSending(true); setSendResp(null);
    try {
      const r = await api.post("/whatsapp/karix/test-send", { to_number: toNum, text });
      setSendResp(r.data);
      api.get("/whatsapp/webhook-logs", { params: { limit: 20 } }).then((r) => setLogs(r.data));
    } catch (e) { setSendResp({ ok: false, error: e?.response?.data?.detail || e.message }); }
    finally { setSending(false); }
  };

  const doTemplate = async () => {
    setTplResp(null);
    let params = {};
    try { params = JSON.parse(tplParams || "{}"); } catch { setTplResp({ ok: false, error: "Invalid JSON parameters" }); return; }
    try {
      const r = await api.post("/whatsapp/karix/test-template", { to_number: tplNum, template_id: tplId, parameters: params });
      setTplResp(r.data);
    } catch (e) { setTplResp({ ok: false, error: e?.response?.data?.detail || e.message }); }
  };

  if (!status) return <Layout title="Karix Setup"><div>Loading…</div></Layout>;

  return (
    <Layout title="Karix Setup" subtitle="WhatsApp Business API">
      {/* Connection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-sharp p-6">
          <div className="flex items-center gap-3 mb-3">
            <Plug size={16} className="text-[#C5A880]" />
            <div className="label-tiny">Endpoint</div>
          </div>
          <div className="text-[13px] break-all font-mono">{status.outbound_endpoint}</div>
        </div>
        <div className="card-sharp p-6">
          <div className="flex items-center gap-3 mb-3">
            <RadioTower size={16} className="text-[#C5A880]" />
            <div className="label-tiny">WABA Number</div>
          </div>
          <div className="text-2xl font-light" style={{fontFamily:"Outfit"}}>+91 {status.waba_number}</div>
          <div className="text-[12px] text-[#8C8C8C] mt-1">API key: {status.api_key_masked}</div>
        </div>
        <div className="card-sharp p-6 bg-[#1A1A1A] text-white">
          <div className="flex items-center gap-3 mb-3">
            <Webhook size={16} className="text-[#C5A880]" />
            <div className="label-tiny" style={{color:"rgba(255,255,255,0.5)"}}>Webhook URL</div>
          </div>
          <div className="text-[12px] break-all font-mono text-white/90">{status.webhook_url}</div>
          <button className="mt-3 text-[11px] flex items-center gap-2 text-[#C5A880] hover:text-white" onClick={copyWebhook} data-testid="copy-webhook-btn">
            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
          </button>
        </div>
      </div>

      {/* Setup checklist */}
      <div className="card-sharp p-6 mt-5">
        <div className="label-tiny">Go-live checklist</div>
        <div className="text-xl font-light mt-1 mb-4" style={{fontFamily:"Outfit"}}>Register with Karix Managebot</div>
        <ol className="space-y-3 text-[13.5px] text-[#3D3328] list-decimal pl-5">
          <li>Log in to Karix Managebot at <a href="https://rcmui.instaalerts.zone/bot_builder_gui/" target="_blank" rel="noreferrer" className="text-[#6B5634] underline">rcmui.instaalerts.zone</a>.</li>
          <li>Open <span className="font-medium">WhatsApp Campaign → Webhook_Config</span> and create a new delivery rule.</li>
          <li>Paste the <span className="font-medium">Webhook URL</span> from the card above. Set <span className="font-mono text-[12px]">Content-Type: application/json</span>.</li>
          <li>Create and submit pre-approved <span className="font-medium">templates</span> for outbound campaigns (welcome, reactivation). Templates can be sent any time; free-form TEXT only works in a 24-hour customer session window.</li>
          <li>Once a customer messages your WABA number (8130790443), all replies from this platform are sent live via Karix.</li>
        </ol>
        <div className="mt-4 flex items-start gap-2 text-[12px] text-[#8A6F1F] bg-[#FBF4E0] p-3 border border-[#F0E4B5]">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div>Karix returns <span className="font-mono">500 Internal System Error</span> when you try to send free-form TEXT to a number with no active 24-hour session. This is per WhatsApp policy. Use a Template message in that case.</div>
        </div>
      </div>

      {/* Test send */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="card-sharp p-6">
          <div className="label-tiny mb-1">Test send</div>
          <div className="text-xl font-light mb-4" style={{fontFamily:"Outfit"}}>Free-form TEXT (24h session)</div>
          <div className="space-y-3">
            <div><div className="label-tiny mb-1.5">Recipient (with country code)</div>
              <input className="fin" placeholder="918130790443" value={toNum} onChange={(e) => setToNum(e.target.value)} data-testid="karix-test-to" />
            </div>
            <div><div className="label-tiny mb-1.5">Message</div>
              <textarea className="fin" rows="3" value={text} onChange={(e) => setText(e.target.value)} data-testid="karix-test-text" />
            </div>
            <button className="btn-dark flex items-center gap-2" disabled={sending || !toNum} onClick={doTestSend} data-testid="karix-test-send"><Send size={14} /> Send via Karix</button>
            {sendResp && <Resp resp={sendResp} />}
          </div>
        </div>

        <div className="card-sharp p-6">
          <div className="label-tiny mb-1">Template send</div>
          <div className="text-xl font-light mb-4" style={{fontFamily:"Outfit"}}>Approved TEMPLATE (any time)</div>
          <div className="space-y-3">
            <div><div className="label-tiny mb-1.5">Recipient</div>
              <input className="fin" placeholder="918130790443" value={tplNum} onChange={(e) => setTplNum(e.target.value)} data-testid="karix-tpl-to" />
            </div>
            <div><div className="label-tiny mb-1.5">Template ID</div>
              <input className="fin" placeholder="e.g. 2003 or welcome_menu" value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="karix-tpl-id" />
            </div>
            <div><div className="label-tiny mb-1.5">Parameters (JSON)</div>
              <textarea className="fin font-mono text-[12px]" rows="2" value={tplParams} onChange={(e) => setTplParams(e.target.value)} />
            </div>
            <button className="btn-dark flex items-center gap-2" disabled={!tplNum || !tplId} onClick={doTemplate} data-testid="karix-tpl-send"><Send size={14} /> Send template</button>
            {tplResp && <Resp resp={tplResp} />}
          </div>
        </div>
      </div>

      {/* Webhook logs */}
      <div className="card-sharp mt-5">
        <div className="p-6 pb-3">
          <div className="label-tiny">Inbound webhook activity</div>
          <div className="text-xl font-light mt-1" style={{fontFamily:"Outfit"}}>Recent webhook hits</div>
        </div>
        <table className="fin">
          <thead><tr><th>Time</th><th>Payload preview</th></tr></thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id}>
                <td className="text-[12px] text-[#8C8C8C] font-mono whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="font-mono text-[11px] text-[#3D3328] truncate" style={{maxWidth:600}}>{JSON.stringify(l.payload).slice(0, 240)}…</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan="2" className="text-center py-8 text-[#8C8C8C]">No webhook activity yet. Once Karix posts to your webhook URL, you'll see it here.</td></tr>}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

function Resp({ resp }) {
  const ok = resp.ok;
  return (
    <div className={`mt-2 p-3 text-[12px] border ${ok ? "border-[#4A6741]/30 bg-[#E8EFE0]" : "border-[#8C3A3A]/30 bg-[#F0E4E0]"}`}>
      <div className={ok ? "text-[#4A6741] font-semibold mb-1" : "text-[#8C3A3A] font-semibold mb-1"}>{ok ? "Sent" : "Failed"}</div>
      <pre className="font-mono text-[11px] whitespace-pre-wrap break-all" data-testid="karix-resp">{JSON.stringify(resp, null, 2)}</pre>
    </div>
  );
}
